// System Health — the whole-system overview (Alert/Metrics unification). Shows
// the honest LIVENESS components (from /health/summary) alongside the unified
// system-metric BANDS (from /health/metrics): each metric's live value against
// its central warn/critical thresholds, with per-scope breaching drill-down and
// a deep-link. Thresholds are edited ONLY in Alerts > Rules — this page links
// there, it never edits. View-only, backend_view.

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { Link } from '@tanstack/react-router';
import { useHealthSummary, useSystemHealth } from '@/api/hooks';
import { QueryErrorNote } from '@/components/error-panel';
import { bandColor, bandLabel, pillClass, type Band } from '@/lib/system-metrics';
import { SyncDot } from '@/components/sync-pill';
import { componentDetail, componentLabel, relTime } from '@/lib/chip-state';
import type {
  ComponentHealth,
} from '@/api/types';
import type {
  HealthStatus,
  StatusBand,
  SystemMetricStatus,
} from '@/api/types.gen';
import { PageActions } from '@/components/page-actions';

function statusToBand(s: HealthStatus): Band {
  if (s === 'down') return 'critical';
  if (s === 'degraded') return 'warn';
  return 'ok';
}

function fmtValue(unit: string, value: number | null): string {
  if (value == null) return '—';
  switch (unit) {
    case 'pct':
      return `${value.toFixed(1)}%`;
    case 'hours':
      return `${value.toFixed(1)}h`;
    case 'secs': {
      const s = Math.round(value);
      if (s < 90) return `${s}s`;
      if (s < 5400) return `${Math.round(s / 60)}m`;
      return `${(s / 3600).toFixed(1)}h`;
    }
    case 'count':
      return `${Math.round(value)}`;
    default:
      return `${value}`;
  }
}

// ── one live component (liveness) ──────────────────────────────────────────────

function ComponentTile({ c, now }: { c: ComponentHealth; now: number }) {
  const band = statusToBand(c.status);
  return (
    <div className="kpi" style={{ borderLeft: `3px solid ${bandColor(band)}` }}>
      <div className="kpi-l">{componentLabel(c.component)}</div>
      <div className="kpi-v" style={{ color: bandColor(band), fontSize: 15 }}>{c.status.toUpperCase()}</div>
      <div className="kpi-s" style={{ textTransform: 'none' }}>
        {componentDetail(c.component, c.detail)}
        {c.last_seen ? ` · ${relTime(+new Date(c.last_seen), now)}` : ''}
      </div>
    </div>
  );
}

// ── one system metric (band vs central thresholds) ─────────────────────────────

function MetricCard({ m, now }: { m: SystemMetricStatus; now: number }) {
  const band = (m.stale ? 'unknown' : (m.band as StatusBand)) as Band;
  const arrow = m.direction === 'higher_is_better' ? '↑ higher is better' : '↓ lower is better';
  return (
    <div
      className={'cp-panel' + (m.stale ? ' cp-low' : '')}
      style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, borderTopColor: bandColor(band) }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{m.label}</div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.04em' }}>{m.key}</div>
        </div>
        <span className={'sync-pill ' + pillClass(band)} style={{ fontSize: 10, padding: '3px 8px' }}>
          <SyncDot band={band}/>{m.stale ? 'Stale' : bandLabel(band)}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: bandColor(band) }}>{fmtValue(m.unit, m.value)}</div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          warn {fmtValue(m.unit, m.warn)} · crit {fmtValue(m.unit, m.critical)}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{arrow}</span>
        <Link to="/backend/alerts" preload="intent" className="btn-ghost" style={{ fontSize: 11 }}>Edit thresholds →</Link>
      </div>
      {m.breaching.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {m.breaching.length} breaching
          </div>
          {m.breaching.slice(0, 6).map((b) => (
            <BreachRow key={b.scope_ref} link={b.link} label={b.label} value={fmtValue(m.unit, b.value)} band={b.band as Band} />
          ))}
        </div>
      )}
    </div>
  );
}

function BreachRow({ link, label, value, band }: { link: string; label: string; value: string; band: Band }) {
  return (
    <Link to={link as never} preload="intent" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, textDecoration: 'none', fontSize: 11.5, color: 'var(--ink)' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <span className="mono" style={{ color: bandColor(band), flexShrink: 0 }}>{value}</span>
    </Link>
  );
}

// ── the page ────────────────────────────────────────────────────────────────

export function SystemHealthView() {
  const now = Date.now();
  const healthQ = useHealthSummary();
  const metricsQ = useSystemHealth();
  const components = healthQ.data?.components ?? [];
  const metrics = metricsQ.data?.metrics ?? [];

  const counts = metrics.reduce(
    (a, m) => {
      const b = m.stale ? 'unknown' : m.band;
      a[b] = (a[b] ?? 0) + 1;
      return a;
    },
    {} as Record<string, number>,
  );
  // The server applies metric staleness before computing `overall`; while the
  // first read is in flight, stay neutral instead of flashing a healthy state.
  const overall = (metricsQ.data?.overall ?? 'unknown') as Band;

  return (
    <div className="cp-page">
      <div className="page-hd">
        <div><h1 className="page-title">System Health</h1></div>
        <PageActions style={{ alignItems: 'center', gap: 10 }}>
          <span className={'sync-pill ' + pillClass(overall)} data-phone="hidden">
            <SyncDot band={overall}/>{bandLabel(overall)}
          </span>
          <span className="mono" data-phone="meta" style={{ fontSize: 10.5, color: 'var(--ink-3)', letterSpacing: '0.06em' }}>
            {counts.ok ?? 0} ok · {counts.warn ?? 0} warn · {counts.critical ?? 0} critical
            {counts.unknown ? ` · ${counts.unknown} stale` : ''}
          </span>
        </PageActions>
      </div>

      <div className="cp-page-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <section>
          <h3 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 10px', letterSpacing: '0.02em' }}>Live components</h3>
          {healthQ.isLoading ? (
            <div className="muted" style={{ padding: '24px 0', fontSize: 12 }}>Loading component health…</div>
          ) : healthQ.isError ? (
            <QueryErrorNote error={healthQ.error} what="system health" onRetry={() => void healthQ.refetch()} />
          ) : (
            <ResponsiveKpis><div className="kpi-strip">
              {components.map((c) => <ComponentTile key={c.component} c={c} now={now} />)}
            </div></ResponsiveKpis>
          )}
        </section>

        <section>
          <h3 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 10px', letterSpacing: '0.02em' }}>Quality metrics</h3>
          {metricsQ.isLoading ? (
            <div className="muted" style={{ padding: '24px 0', fontSize: 12 }}>Loading quality metrics…</div>
          ) : metricsQ.isError ? (
            <QueryErrorNote error={metricsQ.error} what="system metrics" onRetry={() => void metricsQ.refetch()} />
          ) : metrics.length === 0 ? (
            <div className="dp-empty" style={{ padding: '48px 20px' }}>
              <span>No metric snapshot yet</span>
              <span style={{ color: 'var(--ink-3)', textTransform: 'none', letterSpacing: '0.04em' }}>
                The batch evaluator populates bands on its next tick.
              </span>
            </div>
          ) : (
            <div className="cp-area-grid">
              {metrics.map((m) => <MetricCard key={m.key} m={m} now={now} />)}
            </div>
          )}
        </section>
      </div>
      <div className="dp-foot">THRESHOLDS DEFINED ONLY IN ALERTS · SCREENS PAINT THE SERVER BAND</div>
    </div>
  );
}
